import { observer } from "mobx-react-lite";

import { useCellActions } from "../hooks/useCellActions";
import { useCellDisplayData } from "../hooks/useCellDisplayData";
import { BaseCell } from "../primitives/BaseCell";
import { BooleanCell } from "../primitives/BooleanCell";
import { useGridContext } from "../../../hooks/useEvaluateGridContext";

import { ColumnType, ReportCell, ReportColumn } from "@/types/evaluate";
import { truncate } from "@/utils/strings";
import { useReportContext } from "@/components/Evaluate/report-context";
import { cn } from "@/lib/utils";
import FileDisplay from "@/components/ui/FileDisplay";
import { useFileContent } from "@/components/ui/FileDisplay/hooks";
import LoadingSpinner from "@/components/LoadingSpinner";

interface CellProps {
  cell: ReportCell;
  column: ReportColumn;
}

export const BasicCell = observer(({ cell, column }: CellProps) => {
  const { selectedColumn } = useReportContext();
  const { tableStore } = useGridContext();
  const selectedCell = tableStore?.selectedCell;
  const isColumnSelected = selectedColumn?.id === column.id;
  const isPromptOrAgent =
    column.column_type === ColumnType.PROMPT_TEMPLATE || column.column_type === ColumnType.WORKFLOW;
  const { actions } = useCellActions({
    cell,
    column,
    showOpenRequest: isPromptOrAgent,
  });

  const { displayValue, shouldTruncate } = useCellDisplayData(cell, column, false, selectedCell);

  const { fileItems, isFileContent, isProcessingFile } = useFileContent(displayValue);

  if (isProcessingFile) {
    return (
      <BaseCell isSelected={isColumnSelected} actions={actions}>
        <div className="flex h-full items-center justify-center p-2">
          <LoadingSpinner size={4} />
        </div>
      </BaseCell>
    );
  }

  if (isFileContent && fileItems && fileItems.length > 0) {
    return (
      <BaseCell isSelected={isColumnSelected} actions={actions}>
        <div className="overflow-y-auto p-1">
          <div
            className="grid gap-1"
            style={{
              gridTemplateColumns: "repeat(auto-fit, minmax(170px, 200px))",
            }}
          >
            {fileItems.map((item, index: number) => (
              <FileDisplay
                // eslint-disable-next-line react/no-array-index-key
                key={index}
                data={item.data}
                className="max-h-[100px] w-full"
                detectedFile={item.fileType}
              />
            ))}
          </div>
        </div>
      </BaseCell>
    );
  }

  const isBoolean = displayValue.toLowerCase() === "true" || displayValue.toLowerCase() === "false";

  if (isBoolean) {
    return (
      <BooleanCell
        value={displayValue.toLowerCase() === "true"}
        showColors={!isColumnSelected}
        isSelected={isColumnSelected}
        actions={actions}
      />
    );
  }

  const finalDisplayValue = shouldTruncate
    ? truncate(displayValue, column.column_type === ColumnType.DATASET ? 500 : 1500)
    : displayValue;

  const noValue = displayValue === "—";

  return (
    <BaseCell isSelected={isColumnSelected} actions={actions}>
      <div
        className={cn(
          "h-fit w-full whitespace-pre-wrap p-2 text-xs text-gray-700",
          noValue && "flex h-full items-center justify-center text-gray-500",
        )}
      >
        {finalDisplayValue}
      </div>
    </BaseCell>
  );
});
